import { useEffect, useRef, useState } from "react";
import toast from "react-hot-toast";
import * as Sentry from "@sentry/react";

import { useSendOrderEmail } from "@/hooks/useTransbank";
import { validateEmail } from "@/lib";

const GENERIC_ERROR_MESSAGE =
  "No pudimos enviar el correo. Intenta nuevamente o guarda el código QR.";

/**
 * Reenvío del comprobante/QR por correo, compartido por OrderPage y
 * ShareOrderModal (mismo rol que useShareQrImage.js para "compartir").
 *
 * Reemplaza el patrón anterior (toast de éxito incondicional + fallback
 * silencioso a user.usuario_correo cuando el correo escrito era inválido) por:
 * input controlado, validación explícita, guarda de reentrada, y toast/Sentry
 * que dependen del resultado real de la mutación.
 */
export function useSendQrEmail({ pedidoId, codigoQr, correoInicial }) {
  const [email, setEmail] = useState(correoInicial ?? "");
  const [emailError, setEmailError] = useState("");
  const [isSending, setIsSending] = useState(false);
  const isSendingRef = useRef(false);
  const sendOrderEmail = useSendOrderEmail();

  // Si cambia el pedido (p. ej. se reabre el modal con otra compra), resetear
  // el estado del hook para no arrastrar un error o un correo de otro pedido.
  useEffect(() => {
    setEmail(correoInicial ?? "");
    setEmailError("");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pedidoId]);

  const sendEmail = async () => {
    if (isSendingRef.current) return false;

    const trimmedEmail = email.trim().toLowerCase();
    if (!trimmedEmail || !validateEmail(trimmedEmail)) {
      setEmailError("Ingresa un correo válido para enviar el código.");
      return false;
    }
    setEmailError("");

    isSendingRef.current = true;
    setIsSending(true);

    try {
      await sendOrderEmail.mutateAsync({
        pedido_id: pedidoId,
        email: trimmedEmail,
        ...(codigoQr ? { codigo_qr: codigoQr } : {}),
      });
      toast.success("Se ha enviado un correo con los detalles de tu compra");
      return true;
    } catch (error) {
      toast.error(GENERIC_ERROR_MESSAGE);
      Sentry.captureException(error, {
        extra: {
          pedido_id: pedidoId,
          endpoint: "/apiV2/enviar-pedido-email",
          httpStatus: error?.httpStatus,
          backendMessage: error?.backendMessage || error?.message,
        },
      });
      return false;
    } finally {
      isSendingRef.current = false;
      setIsSending(false);
    }
  };

  return { email, setEmail, emailError, isSending, sendEmail };
}
